/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Spin, notification } from 'antd';
import get from 'lodash/get';
import {
  fetchSpacerLogOverview,
  fetchSpacerLogDaily,
  fetchSpacerLogShiftOverview,
} from '../../services/spacerLogDashboardApi';
import SpacerLogOverviewView from './SpacerLogOverviewView';
import SpacerLogDailyView from './SpacerLogDailyView';
import SpacerLogShiftOverviewView from './SpacerLogShiftOverviewView';
import SpacerLogDayToolbar from './SpacerLogDayToolbar';
import {
  stepAnchorDate,
  resolveDailySowId,
} from './spacerLogUtils';
import '../../pages/dashboard/DashboardPage.css';
import './SpacerLogDashboard.css';

const SpacerLogDashboard = ({ selectedProject }) => {
  const projectId = get(selectedProject, 'id');

  const [activeView, setActiveView] = useState('project');
  const [sowId, setSowId] = useState('all');
  const [periodType, setPeriodType] = useState('month');
  const [anchorDate, setAnchorDate] = useState(moment().format('YYYY-MM-DD'));
  const [dailyDate, setDailyDate] = useState(null);
  const [dailySowId, setDailySowId] = useState(null);
  const [shiftFilter, setShiftFilter] = useState('both');

  const [overviewData, setOverviewData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [shiftOverviewData, setShiftOverviewData] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingShiftOverview, setLoadingShiftOverview] = useState(false);
  const [overviewError, setOverviewError] = useState(null);
  const [dailyError, setDailyError] = useState(null);
  const [shiftOverviewError, setShiftOverviewError] = useState(null);

  const loadOverview = useCallback(async () => {
    if (!projectId) return;
    setLoadingOverview(true);
    setOverviewError(null);
    try {
      const data = await fetchSpacerLogOverview({
        projectId,
        periodType,
        anchorDate,
        sowId,
      });
      setOverviewData(data);
    } catch (err) {
      const msg = get(err, 'error', get(err, 'message', 'Failed to load spacer log overview.'));
      setOverviewError(msg);
      notification.error({ message: msg });
    } finally {
      setLoadingOverview(false);
    }
  }, [projectId, periodType, anchorDate, sowId]);

  const loadDaily = useCallback(async () => {
    if (!projectId || !dailyDate || !dailySowId) return;
    setLoadingDaily(true);
    setDailyError(null);
    try {
      const data = await fetchSpacerLogDaily({
        projectId,
        sowId: dailySowId,
        logDate: dailyDate,
      });
      setDailyData(data);
    } catch (err) {
      const msg = get(err, 'error', get(err, 'message', 'Failed to load daily spacer log.'));
      setDailyError(msg);
      notification.error({ message: msg });
    } finally {
      setLoadingDaily(false);
    }
  }, [projectId, dailyDate, dailySowId]);

  const loadShiftOverview = useCallback(async () => {
    if (!projectId || !dailyDate || !dailySowId) return;
    setLoadingShiftOverview(true);
    setShiftOverviewError(null);
    try {
      const data = await fetchSpacerLogShiftOverview({
        projectId,
        sowId: dailySowId,
        logDate: dailyDate,
        shift: shiftFilter,
      });
      setShiftOverviewData(data);
    } catch (err) {
      const msg = get(err, 'error', get(err, 'message', 'Failed to load shift overview.'));
      setShiftOverviewError(msg);
      notification.error({ message: msg });
    } finally {
      setLoadingShiftOverview(false);
    }
  }, [projectId, dailyDate, dailySowId, shiftFilter]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (activeView === 'daily' && dailyDate && dailySowId) {
      loadDaily();
    }
  }, [activeView, dailyDate, dailySowId, loadDaily]);

  useEffect(() => {
    if (activeView === 'shift' && dailyDate && dailySowId) {
      loadShiftOverview();
    }
  }, [activeView, dailyDate, dailySowId, shiftFilter, loadShiftOverview]);

  const meta = overviewData?.meta;
  const dataRange = meta?.dataRange;
  const pipelines = overviewData?.filters?.pipelines || [];

  const handleDayClick = (date) => {
    const resolved = resolveDailySowId(sowId, pipelines);
    if (!resolved) {
      notification.warn({ message: 'Select a pipeline with spacer log data to view daily detail.' });
      return;
    }
    setDailyData(null);
    setShiftOverviewData(null);
    setDailyDate(date);
    setDailySowId(resolved);
    setShiftFilter('both');
    setActiveView('daily');
  };

  const handleLogDateChange = (date) => {
    if (!date) return;
    setDailyData(null);
    setShiftOverviewData(null);
    setDailyDate(date);
  };

  const handleDailySowChange = (nextSowId) => {
    setDailyData(null);
    setShiftOverviewData(null);
    setDailySowId(nextSowId);
  };

  const ensureDailyContext = () => {
    if (dailyDate && dailySowId) return;
    const last = dataRange?.lastLogDate;
    const resolved = resolveDailySowId(sowId, pipelines);
    if (last && resolved) {
      setDailyDate(last);
      setDailySowId(resolved);
    }
  };

  const dayToolbarProps = {
    logDate: dailyDate,
    onLogDateChange: (date) => {
      if (!dailySowId) {
        const resolved = resolveDailySowId(sowId, pipelines);
        if (resolved) setDailySowId(resolved);
      }
      handleLogDateChange(date);
    },
    pipelines,
    sowId: dailySowId,
    onSowChange: handleDailySowChange,
    dataRange,
    shiftFilter,
    onShiftFilterChange: setShiftFilter,
  };

  const showDayToolbar = activeView === 'daily' || activeView === 'shift';

  const canNextPeriod =
    meta?.toDate && dataRange?.lastLogDate
      ? moment(meta.toDate).isBefore(dataRange.lastLogDate, 'day')
      : false;

  const footerLabel = `SPACER LOG · PROJECT ${projectId || '—'}`;

  return (
    <div className="spacer-log-dashboard">
      <div className="spacer-log-dashboard__tabbar">
        <button
          type="button"
          className={`spacer-log-dashboard__tab-btn${activeView === 'project' ? ' spacer-log-dashboard__tab-btn--active' : ''}`}
          onClick={() => setActiveView('project')}
        >
          Project Overview
        </button>
        <button
          type="button"
          className={`spacer-log-dashboard__tab-btn${activeView === 'daily' ? ' spacer-log-dashboard__tab-btn--active' : ''}`}
          onClick={() => {
            ensureDailyContext();
            setActiveView('daily');
          }}
        >
          Daily Detail
        </button>
        <button
          type="button"
          className={`spacer-log-dashboard__tab-btn${activeView === 'shift' ? ' spacer-log-dashboard__tab-btn--active' : ''}`}
          onClick={() => {
            ensureDailyContext();
            setActiveView('shift');
          }}
        >
          Shift Overview
        </button>
      </div>

      {showDayToolbar ? <SpacerLogDayToolbar {...dayToolbarProps} /> : null}

      <Spin
        spinning={
          activeView === 'project'
            ? loadingOverview
            : activeView === 'shift'
              ? loadingShiftOverview
              : loadingDaily
        }
        tip={
          activeView === 'project'
            ? 'Loading project overview…'
            : activeView === 'shift'
              ? 'Loading shift overview…'
              : 'Loading daily detail…'
        }
      >
        <div className="spacer-log-dashboard__content">
          {activeView === 'project' ? (
            overviewError ? (
              <div className="spacer-log-dashboard__error">{overviewError}</div>
            ) : overviewData ? (
              <SpacerLogOverviewView
                data={overviewData}
                sowId={sowId}
                periodType={periodType}
                onSowChange={setSowId}
                onPeriodTypeChange={setPeriodType}
                onPrevPeriod={() => setAnchorDate((d) => stepAnchorDate(d, periodType, -1))}
                onNextPeriod={() => setAnchorDate((d) => stepAnchorDate(d, periodType, 1))}
                canNextPeriod={canNextPeriod}
                onDayClick={handleDayClick}
                isLoading={loadingOverview}
              />
            ) : (
              <div className="spacer-log-dashboard__loading">
                No overview data returned from the API for this project and period.
              </div>
            )
          ) : activeView === 'shift' ? (
            shiftOverviewError ? (
              <div className="spacer-log-dashboard__error">{shiftOverviewError}</div>
            ) : shiftOverviewData || loadingShiftOverview ? (
              <SpacerLogShiftOverviewView
                data={shiftOverviewData}
                isLoading={loadingShiftOverview}
              />
            ) : (
              <div className="spacer-log-dashboard__loading">
                {dailyDate ? 'Loading shift overview…' : 'Select a day from the heatmap or open Shift Overview.'}
              </div>
            )
          ) : dailyError ? (
            <div className="spacer-log-dashboard__error">{dailyError}</div>
          ) : dailyData || loadingDaily ? (
            <SpacerLogDailyView
              data={dailyData}
              shiftFilter={shiftFilter}
              onShiftFilterChange={setShiftFilter}
              isLoading={loadingDaily}
            />
          ) : (
            <div className="spacer-log-dashboard__loading">
              {dailyDate ? 'Loading daily detail…' : 'Select a day from the heatmap or open Daily Detail.'}
            </div>
          )}
        </div>
      </Spin>

      <footer className="spacer-log-dashboard__footer">
        <span>{footerLabel}</span>
        <span>{meta?.updatedAt ? `UPDATED ${meta.updatedAt}` : ''}</span>
      </footer>
    </div>
  );
};

export default SpacerLogDashboard;
